/**
 * Aufrufe des Kundenportals -- F4-17 bis F4-23.
 *
 * **Fast alles davon sind dieselben Endpunkte wie in der Agentenoberflaeche.**
 * Was ein Portalnutzer davon zu sehen bekommt, entscheidet der Server: Die
 * Ticketliste ist auf seinen Kunden und je nach Rolle auf seine eigenen
 * Vorgaenge eingeschraenkt (F4-19), der Verlauf zeigt nur oeffentliche
 * Beitraege (F4-23), die Wissenssuche nur freigegebene Portalartikel (F4-22).
 *
 * Die Oberflaeche verlaesst sich darauf und filtert **nicht** noch einmal.
 * Ein zweiter Filter im Browser waere kein zusaetzlicher Schutz -- er waere
 * die Stelle, an der jemand spaeter glaubt, der erste sei entbehrlich.
 *
 * Eigene Typen statt eines Imports aus `features/tickets`: Das Portal ist ein
 * eigenes Buendel (F4-17). Jeder Import aus der Agentenoberflaeche zoege sie
 * mit hinein.
 */

import type { components } from '../../api/schema';
import { request } from '../../lib/apiClient';

export type PortalSettings = components['schemas']['PortalSettingsRead'];
export type PortalTicket = components['schemas']['TicketRead'];
export type PortalTicketListItem = components['schemas']['TicketListItem'];
export type PortalArticle = components['schemas']['ArticleRead'];
export type PortalKbArticle = components['schemas']['KbArticleListItem'];
export type PortalKbArticleDetail = components['schemas']['KbArticleRead'];
export type PortalForm = components['schemas']['PortalFormRead'];
export type PortalFormField = components['schemas']['FormFieldModel'];

interface TicketPage {
  items: PortalTicketListItem[];
  next_cursor: string | null;
  has_more: boolean;
}

export function fetchPortalSettings(): Promise<PortalSettings> {
  return request<PortalSettings>('/portal/settings');
}

export function listMyTickets(): Promise<TicketPage> {
  return request<TicketPage>('/tickets', { query: { limit: 50, sort: '-updated_at' } });
}

export function getMyTicket(ticketId: string): Promise<PortalTicket> {
  return request<PortalTicket>(`/tickets/${ticketId}`);
}

export function listMyTicketArticles(ticketId: string): Promise<PortalArticle[]> {
  return request<PortalArticle[]>(`/tickets/${ticketId}/articles`);
}

export function createMyTicket(title: string, description: string): Promise<PortalTicket> {
  // Kunde und Melder kommen aus dem Zugang und nicht aus dem Antrag -- der
  // Server setzt beides. Sie hier mitzuschicken waere wirkungslos und
  // erweckte den Eindruck, man koenne sie waehlen.
  return request<PortalTicket>('/tickets', { method: 'POST', body: { title, description } });
}

export function replyToMyTicket(ticketId: string, bodyHtml: string): Promise<PortalArticle> {
  return request<PortalArticle>(`/tickets/${ticketId}/articles`, {
    method: 'POST',
    body: { article_type: 'portal', visibility: 'public', body_html: bodyHtml },
  });
}

/**
 * Die Hilfe im Portal -- F4-22.
 *
 * Der Listenendpunkt ist seit F4-27 cursor-paginiert; das Portal nimmt die
 * erste Seite und laesst es dabei. Wer im Portal sucht, will eine Antwort
 * und keinen Bestand: Zwanzig Treffer sind mehr, als jemand liest, und wenn
 * die Antwort nicht darunter ist, war der Suchbegriff der falsche.
 */
export function searchPortalKnowledge(query: string): Promise<PortalKbArticle[]> {
  return request<{ items: PortalKbArticle[] }>('/kb-articles', {
    query: { q: query, limit: 20 },
  }).then((page) => page.items);
}

/**
 * Loesungsvorschlaege waehrend der Erfassung -- F4-13, K-107.
 *
 * Derselbe Endpunkt wie in der Agentenoberflaeche. Was ein Portalnutzer
 * dabei sieht, entscheidet ``visible_levels`` im Server: veroeffentlichte
 * Artikel der Stufe ``portal`` oder ``public`` und sonst nichts. Die
 * Sichtbarkeitsregel steht an genau einer Stelle -- eine zweite hier waere
 * eine zweite Wahrheit, und der Unterschied waere ein interner Artikel im
 * Portal.
 */
export function suggestPortalArticles(text: string): Promise<PortalKbArticle[]> {
  return request<PortalKbArticle[]>('/kb-articles/suggestions', { query: { text } });
}

export function getPortalArticle(articleId: string): Promise<PortalKbArticleDetail> {
  return request<PortalKbArticleDetail>(`/kb-articles/${articleId}`);
}

export function listPortalForms(): Promise<PortalForm[]> {
  return request<PortalForm[]>('/portal/forms');
}

export function submitPortalForm(
  formId: string,
  values: { subject: string; message: string; answers: Record<string, unknown> },
): Promise<{ ticket_id: string }> {
  return request<{ ticket_id: string }>(`/portal/forms/${formId}/submit`, {
    method: 'POST',
    body: values,
  });
}
